import axios from 'axios';
import { getUrl } from '../service-utils';
import authHeader from '../auth-header';
import { ITripDestination } from '../../components/modals/trip/use-add-trip';

// Backend interface that matches the API
interface IBackendTripDestination {
  notes?: string;
  placesId?: string;
  latitude?: number;
  longitude?: number;
  name?: string;
  googlePlacesId?: string;
  types?: string[];
  addressComponents?: any[];
}

class TripDestinationService {
  private API_URL: string;

  constructor() {
    this.API_URL = getUrl();
  }

  /**
   * Transform frontend destination to backend format
   */
  private transformToBackend(
    destination: ITripDestination
  ): IBackendTripDestination {
    return {
      notes: '',
      placesId: destination.placesId,
      googlePlacesId: destination.googlePlacesId,
      latitude: destination.latitude,
      longitude: destination.longitude,
      name: destination.name,
      types: destination.types,
      addressComponents: destination.addressComponents
    };
  }

  /**
   * Add destinations to a trip's embedded destinations array
   */
  public async addDestinationsToTrip(
    tripId: string,
    destinations: ITripDestination[]
  ) {
    try {
      const backendDestinations = destinations.map((dest) =>
        this.transformToBackend(dest)
      );
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/destinations`,
        { destinations: backendDestinations },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error adding destinations:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Update a specific destination in a trip
   */
  public async updateDestination(
    tripId: string,
    destinationIndex: number,
    destination: Partial<ITripDestination>
  ) {
    try {
      const backendDestination = this.transformToBackend(
        destination as ITripDestination
      );
      const response = await axios.put(
        `${this.API_URL}trips/${tripId}/destinations/${destinationIndex}`,
        backendDestination,
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error updating destination:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Remove a destination from a trip
   */
  public async removeDestination(tripId: string, destinationIndex: number) {
    try {
      const response = await axios.delete(
        `${this.API_URL}trips/${tripId}/destinations/${destinationIndex}`,
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error removing destination:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Add folder reference to destination's activities, accommodations, or restaurants
   */
  public async addFolderToDestination(
    tripId: string,
    destinationIndex: number,
    folderId: string,
    folderType: 'activities' | 'accommodations' | 'restaurants'
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/destinations/${destinationIndex}/folders`,
        { folderId, folderType },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error adding folder to destination:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Remove folder reference from destination's activities, accommodations, or restaurants
   */
  public async removeFolderFromDestination(
    tripId: string,
    destinationIndex: number,
    folderId: string,
    folderType: 'activities' | 'accommodations' | 'restaurants'
  ) {
    try {
      const response = await axios.delete(
        `${this.API_URL}trips/${tripId}/destinations/${destinationIndex}/folders`,
        {
          data: { folderId, folderType },
          headers: authHeader()
        }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error removing folder from destination:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Create destination folders (legacy method)
   */
  public async createDestinationFolders(tripId: string, destinations: any[]) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/destinations/folders`,
        destinations,
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error creating destination folders:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Copy destinations from one trip to another trip
   * @param sourceTripId - Source trip ID
   * @param targetTripId - Target trip ID
   * @param destinationIds - Optional array of specific destination IDs to copy. If not provided, all destinations will be copied
   */
  public async copyDestinationsToTrip(
    sourceTripId: string,
    targetTripId: string,
    destinationIds?: string[]
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${targetTripId}/destinations/copy`,
        {
          sourceTripId,
          targetTripId,
          destinationIds
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error copying destinations to trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Move destinations from one trip to another trip
   * @param sourceTripId - Source trip ID
   * @param targetTripId - Target trip ID
   * @param destinationIds - Optional array of specific destination IDs to move. If not provided, all destinations will be moved
   */
  public async moveDestinationsToTrip(
    sourceTripId: string,
    targetTripId: string,
    destinationIds?: string[]
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${targetTripId}/destinations/move`,
        {
          sourceTripId,
          targetTripId,
          destinationIds
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error moving destinations to trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Copy a specific destination to another trip
   * @param destinationId - ID of the destination to copy
   * @param sourceTripId - Source trip ID
   * @param targetTripId - Target trip ID
   */
  public async copyDestination(
    destinationId: string,
    sourceTripId: string,
    targetTripId: string
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${targetTripId}/destinations/copy`,
        {
          sourceTripId,
          targetTripId,
          destinationIds: [destinationId]
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error copying destination:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Move a specific destination to another trip
   * @param destinationId - ID of the destination to move
   * @param sourceTripId - Source trip ID
   * @param targetTripId - Target trip ID
   */
  public async moveDestination(
    destinationId: string,
    sourceTripId: string,
    targetTripId: string
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${targetTripId}/destinations/move`,
        {
          sourceTripId,
          targetTripId,
          destinationIds: [destinationId]
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error moving destination:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }
  /**
   * Validate that a place resolves to a city or country level destination.
   * No write occurs — used to confirm a destination before adding activities.
   * Replaces the old addDestinations write endpoint.
   */
  public async validateDestination(
    tripId: string,
    googlePlacesId: string
  ): Promise<{ valid: boolean; destination?: any; reason?: string }> {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/validate-destination`,
        { googlePlacesId },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error validating destination:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }
}

const tripDestinationService = new TripDestinationService();
export default tripDestinationService;
